import { Add, ContentCopy, RemoveCircleOutline } from '@mui/icons-material';
import { Grid } from '@mui/material';
import classNames from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Clock } from '~/Components/Icons';
import { useDateFns } from '~/Hooks';
import { WeekDay } from '~/Models/DTO/Provider.model';
import { Button, IconButton, KeyboardTimePicker, Switch } from '~/UIKit';
import { OpeningHoursSectionStyles } from '..';
import { IErrorWeekdays } from '../OpeningHoursSection';

export interface IOpeningHoursSectionTableRowProps {
  dataTable: WeekDay[];
  currentDay: string;
  dayIndex: number;
  errorWeekdays: IErrorWeekdays;
  onToggleDay: (day: string, timeIndex?: number) => void;
  onTimeChange: (day: string, timeRowIndex: number, type: 'from' | 'to', value: any) => void;
  onAddTime: (day: string) => void;
  onCopyToAll: () => void;
}

export const OpeningHoursSectionTableRow = ({
  dataTable,
  currentDay,
  dayIndex,
  errorWeekdays,
  onToggleDay,
  onTimeChange,
  onAddTime,
  onCopyToAll,
}: IOpeningHoursSectionTableRowProps) => {
  const { t } = useTranslation();
  const { classes } = OpeningHoursSectionStyles();
  const { convertTimeStringToDate } = useDateFns();
  return (
    <React.Fragment>
      {dataTable.map((_dayRow, dayRowIndex) => (
        <Grid data-testid="opening-hours-table-row" container key={dayRowIndex}>
          <Grid
            className={classNames({
              [classes.inactive]: dataTable.length === 0,
              [classes.hasBottomBorder]: dayRowIndex === dataTable.length - 1,
            })}
            container
            md={3}
            alignSelf="normal"
            alignItems="self-start"
          >
            {dayRowIndex === 0 && (
              <Grid
                className={classNames(classes.dayRow)}
                key={dayRowIndex}
                alignItems="center"
                container
              >
                <Grid className={classes.iconWrapper}>
                  <Clock />
                </Grid>
                <span className={classes.itemRow}>{t(`app.weekdays.${currentDay}`)}</span>
              </Grid>
            )}
          </Grid>
          <Grid
            container
            md={1}
            justifyContent="center"
            className={classNames({
              [classes.inactive]: dataTable.length === 0,
              [classes.hasBottomBorder]: dayRowIndex === dataTable.length - 1,
            })}
          >
            <Grid key={dayRowIndex} container className={classes.row} justifyContent="center">
              {dayRowIndex === 0 ? (
                <Switch checked={dataTable.length > 0} onChange={() => onToggleDay(currentDay)} />
              ) : null}
            </Grid>
          </Grid>
          <Grid
            md={5}
            container
            data-testid="opening-hours-section-time-block"
            className={classes.hasBottomBorder}
          >
            <Grid key={dayRowIndex} container md={12}>
              <Grid item md={2} />
              <Grid item md={4}>
                <KeyboardTimePicker
                  inputClasses={classes.timePicker}
                  inputFormat="HH:mm"
                  name={`fromName-${_dayRow}`}
                  error={
                    !errorWeekdays[currentDay]?.[dayRowIndex]?.isValid &&
                    errorWeekdays[currentDay]?.[dayRowIndex]?.type === 0
                  }
                  value={convertTimeStringToDate(_dayRow?.from?.toString()).toISOString() || ''}
                  onDateChange={(_, value) => onTimeChange(currentDay, dayRowIndex, 'from', value)}
                />
              </Grid>
              <Grid item md={4}>
                <KeyboardTimePicker
                  inputClasses={classes.timePicker}
                  inputFormat="HH:mm"
                  value={convertTimeStringToDate(_dayRow?.to?.toString()).toISOString() || ''}
                  onDateChange={(_, value) => onTimeChange(currentDay, dayRowIndex, 'to', value)}
                />
              </Grid>
              <Grid item md={2} />
            </Grid>
          </Grid>
          <Grid data-testid="opening-hours-section-action-col" container md={3} alignSelf="start">
            <Grid
              container
              alignItems="center"
              justifyContent="center"
              md={12}
              className={classNames(classes.row, classes.actionCol)}
            >
              <Grid container alignItems="center" justifyContent="center" md={3}>
                {dayRowIndex === 0 ? (
                  <IconButton
                    onClick={() => onAddTime(currentDay)}
                    className={classes.addButton}
                    size="medium"
                  >
                    <Add />
                  </IconButton>
                ) : (
                  <IconButton
                    className={classes.deleteButton}
                    onClick={() => onToggleDay(currentDay, dayRowIndex)}
                    size="medium"
                  >
                    <RemoveCircleOutline />
                  </IconButton>
                )}
              </Grid>
              <Grid container item md={8} justifyContent="flex-end">
                {dayIndex === 0 && dayRowIndex === 0 && (
                  <Button variant="text" className={classes.copyButton} onClick={onCopyToAll}>
                    <ContentCopy />
                    {t('app.copyToAll')}
                  </Button>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      ))}
    </React.Fragment>
  );
};
